/**
 * Anonymous-call service. Wraps the Firebase Cloud Functions that drive
 * Twilio (initiate / cancel / history). Replaces `anonymous-calls.js`.
 *
 * @typedef {Object} InitiateResult
 * @property {boolean} ok
 * @property {string} [callSid]
 * @property {string} [errorCode]
 * @property {string} [message]
 */

import { getFirebase, auth } from '../../core/firebase.js';
import { Firebase } from '../../app/config.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { createLogger } from '../../core/logger.js';
import * as bus from '../../core/eventBus.js';

const log = createLogger('call');

let inProgress = false;
/** @type {string | null} */
let currentCallSid = null;

/** @returns {boolean} */
export function isInProgress() { return inProgress; }

/** @returns {string | null} */
export function getCurrentSid() { return currentCallSid; }

/**
 * Initiate an anonymous call to the owner of the given plate.
 * Returns a result object. UI hooks (calling screen, dial tone) are wired in
 * the view layer — keep this service UI-agnostic.
 *
 * @param {string} targetPlate
 * @returns {Promise<InitiateResult>}
 */
export async function initiate(targetPlate) {
  const plate = normalizePlate(targetPlate);
  if (!plate) return { ok: false, errorCode: 'invalid-argument', message: 'מספר רכב לא תקין.' };
  if (!auth().currentUser) return { ok: false, errorCode: 'unauthenticated', message: 'יש להתחבר כדי להתקשר.' };
  if (inProgress) return { ok: false, errorCode: 'busy', message: 'שיחה כבר מתבצעת.' };

  inProgress = true;
  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    const initiateCall = functions.httpsCallable('initiateAnonymousCall');
    const result = await initiateCall({ plate });
    if (result?.data?.success) {
      currentCallSid = String(result.data.callSid ?? '');
      return { ok: true, callSid: currentCallSid };
    }
    inProgress = false;
    return { ok: false, message: result?.data?.message ?? 'שגיאה לא ידועה.' };
  } catch (err) {
    inProgress = false;
    const e = /** @type {any} */ (err);
    log.warn('initiate failed:', e);
    return { ok: false, errorCode: e?.code, message: errorToMessage(e) };
  }
}

/**
 * Cancel the in-flight call. Best-effort — server may have already connected.
 * @returns {Promise<boolean>}
 */
export async function cancel() {
  if (!currentCallSid) {
    markEnded();
    return false;
  }
  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    await functions.httpsCallable('cancelCall')({ callSid: currentCallSid });
    return true;
  } catch (err) {
    log.warn('cancel failed:', err);
    return false;
  } finally {
    markEnded();
  }
}

/** Mark the call as ended locally and emit the bus event. */
export function markEnded() {
  inProgress = false;
  currentCallSid = null;
  bus.emit('call:ended', undefined);
}

/** Fetch the user's call history via the `getUserCallHistory` Cloud Function. */
export async function getHistory() {
  try {
    const functions = getFirebase().app().functions(Firebase.functionsRegion);
    const result = await functions.httpsCallable('getUserCallHistory')({});
    if (result?.data?.success) return Array.isArray(result.data.history) ? result.data.history : [];
    return [];
  } catch (err) {
    log.warn('getHistory failed:', err);
    return [];
  }
}

/**
 * Map a Firebase Functions error to a Hebrew user-facing string.
 * @param {{ code?: string; message?: string }} err
 */
function errorToMessage(err) {
  switch (err?.code) {
    case 'unauthenticated': return 'יש להתחבר כדי להתקשר.';
    case 'not-found': return 'הרכב לא נמצא במערכת.';
    case 'resource-exhausted': return err.message || 'ניתן להתקשר רק פעם בשעה לאותו נהג.';
    case 'invalid-argument': return err.message || 'נתונים לא תקינים.';
    default: return err?.message || 'שגיאה ביצירת השיחה.';
  }
}

// Register one extra bus event type for call lifecycle (parallel to messaging).
// (TS-wise — declared inline via the `void` cast since we don't extend AppEvents here.)
void bus;
